// BFF Authentication Service - uses cookies instead of tokens
export class AuthServiceBFF {
  private baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  private csrfToken: string | null = null;

  async checkAuthStatus(): Promise<{ isAuthenticated: boolean; name?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/status`, {
        credentials: 'include', // Important: include cookies
      });
      
      if (response.ok) {
        return await response.json();
      }
      
      return { isAuthenticated: false };
    } catch (error) {
      console.error('Error checking auth status:', error);
      return { isAuthenticated: false };
    }
  }

  async getUserInfo(): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/user`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        return await response.json();
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user info:', error);
      return null;
    }
  }

  /**
   * Sends the browser to the backend, which challenges Azure AD and brings it
   * back to wherever it started.
   *
   * The default matters: CiviCRM links staff straight to
   * /ta-locals?listing=9196, and most of the time they are not signed in yet.
   * Without a returnUrl the backend falls back to "/" and the listing they
   * clicked is simply lost. Both callers - the sign-in button and the 401
   * interceptor - pass nothing, so capturing it here fixes both.
   */
  async login(returnUrl?: string): Promise<void> {
    const here = returnUrl
      ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
    // Only a path of our own. "//evil.test" is a protocol-relative URL, not a
    // local path, and the backend pastes this straight onto its frontend host.
    const safe = here.startsWith('/') && !here.startsWith('//') ? here : '/';
    window.location.href = `${this.baseUrl}/api/auth/login?returnUrl=${encodeURIComponent(safe)}`;
  }

  async logout(): Promise<void> {
    try {
      await this.getCsrfToken(); // Get CSRF token first
      
      const response = await fetch(`${this.baseUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': this.csrfToken || '',
        },
      });
      
      if (response.ok) {
        // Redirect to home page after logout
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  async getCsrfToken(): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/csrf-token`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        this.csrfToken = data.token;
        return data.token;
      }
      
      return '';
    } catch (error) {
      console.error('Error getting CSRF token:', error);
      return '';
    }
  }

  getCachedCsrfToken(): string | null {
    return this.csrfToken;
  }
}

// Create a singleton instance
export const authService = new AuthServiceBFF();